import type MvArea from "models/MvArea";
import type MvAdressResult from "models/MvAddressResult";
import type MvInputParams from "models/MvInputParams";
import type MvSelectedGroup from "models/MvSelectedGroup";
import { getInitialSelectedGroups } from "./selectionGroups";
import { withoutKeys } from "./helpers";
import MvAddressSelectorSDKOptions from "models/MvAddressSelectorSDKOptions";
import { getPricePerAddress } from "services/pricing";
import {
    filterAreasBySelectedGroups,
    totalAddressCount,
} from "services/selectionHelpers";

export interface SDKState {
    areas: MvArea[];
    activeAreaId: string | null;
    selectedGroups: MvSelectedGroup[];
    panelOpen: boolean;
    showSummary: boolean;
    totalSelected: number;
    pricePerAddress: number;
    selection: MvArea[];
}

export type SDKAction =
    | { type: "addArea"; id: string; defaults: MvInputParams }
    | {
          type: "addMapAddress";
          id: string;
          address: MvAdressResult;
          defaults: MvInputParams;
      }
    | { type: "moveMapAddress"; id: string; address: MvAdressResult }
    | { type: "selectMapArea"; id: string }
    | { type: "selectListArea"; id: string }
    | { type: "removeArea"; id: string }
    | { type: "setAddress"; id: string; address: MvAdressResult | null }
    | { type: "updateParams"; id: string; patch: Partial<MvInputParams> }
    | { type: "receiveAddresses"; id: string; addresses: MvAdressResult[] }
    | {
          type: "setGroups";
          groups:
              | MvSelectedGroup[]
              | ((previous: MvSelectedGroup[]) => MvSelectedGroup[]);
      }
    | { type: "setPanelOpen"; open: boolean }
    | { type: "showSummary" }
    | { type: "hideSummary" };

export const createSDKState = (
    areas: MvArea[],
    options: MvAddressSelectorSDKOptions,
): SDKState => ({
    areas,
    activeAreaId: areas[0]?.id ?? null,
    selectedGroups: [],
    panelOpen: false,
    showSummary: false,
    totalSelected: 0,
    pricePerAddress:
        options?.pricePerAddress ??
        options?.tieredPrices?.[0]?.pricePerAddress ??
        0,
    selection: [],
});

export const sdkReducer = (
    state: SDKState,
    action: SDKAction,
    options: MvAddressSelectorSDKOptions,
    configuration: any = {},
): SDKState => {
    const minimum = options?.zipCodeAreaMinCount;
    const withAreas = (areas: MvArea[]): SDKState =>
        withSelection({
            ...state,
            areas,
            activeAreaId: areas?.find((a: any) => a.isSelected)?.id || null,
            selectedGroups: getInitialSelectedGroups(areas, minimum),
        });
    const withSelection = (state: SDKState): SDKState => {
        const selection = filterAreasBySelectedGroups(
            state.selectedGroups,
            state.areas,
            configuration,
        );
        const totalSelected = totalAddressCount(selection);

        return {
            ...state,
            selection,
            totalSelected,
            pricePerAddress: getPricePerAddress(totalSelected, options),
        };
    };

    switch (action.type) {
        case "addArea": {
            const source =
                state.areas.find((area) => area.id === state.activeAreaId)
                    ?.inputParams ?? action.defaults;
            const inputParams = {
                ...withoutKeys(source, [
                    "houseNumber",
                    "streetName",
                    "postalCode",
                ]),
                radius: action.defaults.radius,
            };
            return {
                ...withAreas([
                    ...state.areas,
                    {
                        id: action.id,
                        inputParams,
                        result: null,
                        disabled: false,
                        addresses: [],
                    },
                ]),
                activeAreaId: action.id,
            };
        }
        case "addMapAddress":
            if (
                state.areas.some(
                    (area) => area.result?.id === action.address.id,
                )
            )
                return state;
            return withAreas([
                ...state.areas.map((area) => ({ ...area, isSelected: false })),
                {
                    id: action.id,
                    inputParams: { ...action.defaults },
                    result: action.address,
                    disabled: false,
                    isSelected: true,
                    addresses: [],
                },
            ]);
        case "moveMapAddress":
            if (
                !state.areas.some((area) => area.id === action.id) ||
                state.areas.some(
                    (area) => area.result?.id === action.address.id,
                )
            )
                return state;
            return withAreas(
                state.areas.map((area) =>
                    area.id === action.id
                        ? {
                              ...area,
                              result: action.address,
                              isSelected: true,
                              inputParams: {
                                  ...area.inputParams,
                                  houseNumber: action.address.huisnr,
                                  postalCode: action.address.postcode,
                              },
                          }
                        : { ...area, isSelected: false },
                ),
            );
        case "selectMapArea":
            return withAreas(
                state.areas.map((area) => ({
                    ...area,
                    isSelected: area.id === action.id,
                })),
            );
        case "selectListArea":
            return withAreas(
                state.areas.map((area) => ({
                    ...area,
                    isSelected: area.id === action.id,
                })),
            );
        case "removeArea":
            return withAreas(
                state.areas.filter((area) => area.id !== action.id),
            );
        case "setAddress":
        case "updateParams":
        case "receiveAddresses": {
            if (!state.areas.some((area) => area.id === action.id))
                return state;
            const existingIds = new Set(
                state.areas
                    .filter((area) => area.id !== action.id)
                    .flatMap((area) =>
                        area.addresses.map((address) => address.id),
                    ),
            );
            return withAreas(
                state.areas.map((area) => {
                    if (area.id !== action.id) return area;
                    if (action.type === "setAddress")
                        return { ...area, result: action.address };
                    if (action.type === "updateParams")
                        return {
                            ...area,
                            inputParams: {
                                ...area.inputParams,
                                ...action.patch,
                            },
                        };
                    return {
                        ...area,
                        addresses: action.addresses.filter(
                            (address) => !existingIds.has(address.id),
                        ),
                    };
                }),
            );
        }
        case "setGroups":
            return withSelection({
                ...state,
                selectedGroups:
                    typeof action.groups === "function"
                        ? action.groups(state.selectedGroups)
                        : action.groups,
            });
        case "setPanelOpen":
            return { ...state, panelOpen: action.open };
        case "showSummary":
            return { ...state, showSummary: true, panelOpen: true };
        case "hideSummary":
            return { ...state, showSummary: false, panelOpen: false };
    }
};
